import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { CompanyDetailsForm } from "@/components/CompanyDetailsForm";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function OrgSettings() {
  const { singleCompany, loading: companiesLoading } = useCompany() || {};
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/admin/org-settings").then(({ data }) => setForm(data)).catch((err) => toast.error(getErrorMessage(err))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/org-settings", {
        fiscal_year_start_month: form.fiscal_year_start_month,
        indent_to_po_tolerance_pct: form.indent_to_po_tolerance_pct,
        po_to_grn_tolerance_pct: form.po_to_grn_tolerance_pct,
        bill_rate_tolerance_pct: form.bill_rate_tolerance_pct,
        allow_negative_stock: form.allow_negative_stock,
        block_self_approval: !!form.block_self_approval,
        enforce_control_gates: form.enforce_control_gates !== false,
        require_bill_verification: !!form.require_bill_verification,
        mfa_policy: form.mfa_policy || "OFF",
        mfa_methods: form.mfa_methods || "APP",
        shift_start: form.shift_start || "09:30",
        late_grace_minutes: Number(form.late_grace_minutes ?? 15),
        shift_end: form.shift_end || "18:30",
        full_day_hours: String(form.full_day_hours ?? 8),
        half_day_hours: String(form.half_day_hours ?? 4),
        weekly_off_days: form.weekly_off_days || "",
        timezone: form.timezone || "Asia/Kolkata",
        decimal_places: form.decimal_places,
      });
      setForm(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      const { data } = await api.post("/admin/org-settings/smtp-test", { to_email: testEmail });
      toast.success(data.detail);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  if (!form) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="org-settings-page">
      <PageHeader title="Organization Settings" description="Your organisation's details, fiscal year, matching tolerances, stock policy and SMTP." />
      <div className="max-w-3xl space-y-4 rounded-xl border border-border bg-card p-6" data-testid="organisation-details-card">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground">Organisation details</h2>
          <p className="mt-1 text-sm text-muted-foreground">Your legal name, tax numbers and contact details. They are kept on your company record and used on documents and reports.</p>
        </div>
        {companiesLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : singleCompany ? (
          <CompanyDetailsForm company={singleCompany} />
        ) : (
          <p className="text-sm text-muted-foreground">Your organisation has several companies, so each keeps its own details. Edit them on the <Link to="/erp/operations/companies" className="font-medium text-foreground underline">Companies</Link> page.</p>
        )}
      </div>
      <div className="max-w-xl space-y-5 rounded-xl border border-border bg-card p-6">
        <div>
          <Label>Fiscal year starts in</Label>
          <Select value={String(form.fiscal_year_start_month)} onValueChange={(v) => setForm({ ...form, fiscal_year_start_month: Number(v) })}>
            <SelectTrigger className="mt-1.5" data-testid="fiscal-year-start-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {/* "Indent → PO tolerance" and "Decimal places" are not shown: nothing in the app applies them yet, so a value here would mislead. */}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>PO → GRN tolerance %</Label><Input type="number" step="0.5" value={form.po_to_grn_tolerance_pct} onChange={(e) => setForm({ ...form, po_to_grn_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="po-to-grn-tolerance-input" /></div>
          <div><Label>Bill rate tolerance %</Label><Input type="number" step="0.5" value={form.bill_rate_tolerance_pct} onChange={(e) => setForm({ ...form, bill_rate_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="bill-rate-tolerance-input" /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Allow negative stock</p>
            <p className="text-xs text-muted-foreground">Lets goods issues, returns, transfers and production push a warehouse balance below zero.</p>
          </div>
          <Switch checked={form.allow_negative_stock} onCheckedChange={(v) => setForm({ ...form, allow_negative_stock: v })} data-testid="allow-negative-stock-switch" />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Block self-approval</p>
            <p className="text-xs text-muted-foreground">Nobody can approve a document they submitted themselves; another person has to. Turn this off only for a one-person office.</p>
          </div>
          <Switch checked={!!form.block_self_approval} onCheckedChange={(v) => setForm({ ...form, block_self_approval: v })} data-testid="block-self-approval-switch" />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Enforce control gates</p>
            <p className="text-xs text-muted-foreground">A new project stays in Planning until it has a signed contract (or an approved exception), its scope and an approved baseline budget. Purchase orders and work orders need a project and pass a budget check. Projects already running are not affected.</p>
          </div>
          <Switch checked={form.enforce_control_gates !== false} onCheckedChange={(v) => setForm({ ...form, enforce_control_gates: v })} data-testid="enforce-control-gates-switch" />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">When a shift starts</p>
            <p className="text-xs text-muted-foreground">Someone is counted late when their check-in is after this time plus the grace. It feeds the late-arrival rate on the KPI page.</p>
          </div>
          <div className="flex items-end gap-2">
            <div><Label className="text-xs">Shift starts</Label><Input type="time" value={form.shift_start || "09:30"} onChange={(e) => setForm({ ...form, shift_start: e.target.value })} className="mt-1 w-32" data-testid="shift-start-input" /></div>
            <div><Label className="text-xs">Grace (minutes)</Label><Input type="number" min="0" max="180" value={form.late_grace_minutes ?? 15} onChange={(e) => setForm({ ...form, late_grace_minutes: e.target.value })} className="mt-1 w-24" data-testid="late-grace-input" /></div>
          </div>
        </div>
        <div className="space-y-3 rounded-lg border border-border p-3" data-testid="working-hours">
          <div><p className="text-sm text-foreground">Working hours</p>
            <p className="text-xs text-muted-foreground">Clock-in and clock-out mark the attendance sheet with these. At least the full-day hours is Present, at least the half-day hours is a Half day, less is Absent. Time after the shift ends is overtime, in half hours.</p></div>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Shift ends</Label><Input type="time" value={form.shift_end || "18:30"} onChange={(e) => setForm({ ...form, shift_end: e.target.value })} className="mt-1 w-32" data-testid="shift-end-input" /></div>
            <div><Label className="text-xs">Full day (hours)</Label><Input type="number" min="1" max="24" step="0.5" value={form.full_day_hours ?? 8} onChange={(e) => setForm({ ...form, full_day_hours: e.target.value })} className="mt-1 w-28" data-testid="full-day-input" /></div>
            <div><Label className="text-xs">Half day (hours)</Label><Input type="number" min="0" max="24" step="0.5" value={form.half_day_hours ?? 4} onChange={(e) => setForm({ ...form, half_day_hours: e.target.value })} className="mt-1 w-28" data-testid="half-day-input" /></div>
            <div><Label className="text-xs">Time zone</Label>
              <Select value={form.timezone || "Asia/Kolkata"} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                <SelectTrigger className="mt-1 w-48" data-testid="timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Asia/Kolkata", "Asia/Dubai", "Asia/Riyadh", "Asia/Qatar", "Asia/Singapore", "Europe/London", "UTC"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-xs">Weekly off days (leave is not counted on these)</Label>
            <div className="mt-1 flex flex-wrap gap-3">{["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
              <label key={d} className="flex items-center gap-1.5 text-sm">
                <Checkbox checked={(form.weekly_off_days || "").split(",").includes(d)} data-testid={`weekly-off-${d.toLowerCase()}`}
                  onCheckedChange={(v) => { const on = new Set((form.weekly_off_days || "").split(",").filter(Boolean)); v ? on.add(d) : on.delete(d);
                    setForm({ ...form, weekly_off_days: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].filter((x) => on.has(x)).join(",") }); }} />
                {d.charAt(0) + d.slice(1).toLowerCase()}</label>))}</div></div>
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Two-step sign-in</p>
            <p className="text-xs text-muted-foreground">Sign in with a password plus a code from an authenticator app. Anyone can turn it on for themselves; this sets who must. People who must are asked to set it up at their next sign-in.</p>
          </div>
          <Select value={form.mfa_policy || "OFF"} onValueChange={(v) => setForm({ ...form, mfa_policy: v })}>
            <SelectTrigger className="w-56" data-testid="mfa-policy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="OFF">Optional for everyone</SelectItem>
              <SelectItem value="ADMINS">Required for admins and finance roles</SelectItem>
              <SelectItem value="ALL">Required for everyone</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-lg border border-border p-3" data-testid="mfa-methods">
          <p className="text-sm text-foreground">Ways to get the code</p>
          <p className="text-xs text-muted-foreground">The authenticator app is always allowed. E-mail and text-message codes are weaker (a stolen mailbox or SIM gives both steps), so admins and finance roles stay on the app whatever you tick. Text messages also need the server's Twilio SMS number to be set.</p>
          <div className="mt-2 flex flex-wrap gap-6">
            {[["EMAIL", "E-mail a code"], ["SMS", "Text message a code"]].map(([m, label]) => (
              <label key={m} className="flex items-center gap-2 text-sm">
                <Checkbox checked={(form.mfa_methods || "APP").split(",").includes(m)} data-testid={`mfa-method-${m.toLowerCase()}`}
                  onCheckedChange={(v) => {
                    const on = new Set((form.mfa_methods || "APP").split(","));
                    v ? on.add(m) : on.delete(m);
                    setForm({ ...form, mfa_methods: ["APP", "EMAIL", "SMS"].filter((x) => x === "APP" || on.has(x)).join(",") });
                  }} />
                {label}</label>))}
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Verify vendor bills before posting</p>
            <p className="text-xs text-muted-foreground">A purchase bill or service bill has to be checked by someone with the Verify action (for example a Finance Approver) before it can be posted. Editing a verified bill takes the check away. Bills already posted are not affected.</p>
          </div>
          <Switch checked={!!form.require_bill_verification} onCheckedChange={(v) => setForm({ ...form, require_bill_verification: v })} data-testid="require-bill-verification-switch" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-org-settings-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings</Button>

        <div className="border-t border-border pt-4">
          <Label>SMTP test</Label>
          <div className="mt-1.5 flex gap-2">
            <Input type="email" placeholder="you@company.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="smtp-test-email-input" />
            <Button variant="outline" onClick={sendTest} disabled={testing || !testEmail} data-testid="smtp-test-send-button">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send test</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
